import type { AthenaRightKey } from "../../rights/key.ts";

export type AthenaAuthorizationScopeKind = "self" | "organization" | "platform";
export type AthenaAuthorizationRiskLevel = "low" | "elevated" | "critical";
export type AthenaAuthorizationRoleScope = "platform" | "organization";

export interface AthenaAuthorizationRightDefinition {
	assignable: boolean;
	description: string;
	displayName: string;
	domain: string;
	key: AthenaRightKey;
	riskLevel: AthenaAuthorizationRiskLevel;
	scopeKind: AthenaAuthorizationScopeKind;
}

export interface AthenaAuthorizationRoleRecord {
	assignable: boolean;
	id: string;
	key: string;
	name: string;
	organizationId: string | null;
	protected: boolean;
	scopeKind: AthenaAuthorizationRoleScope;
	systemKind: "owner" | "admin" | "member" | null;
	version: number;
}

export interface AssignedRoleSummary {
	displayName: string;
	key: string;
	scopeKind: AthenaAuthorizationRoleScope;
}

export interface RoleDescriptor {
	assignable: boolean;
	assignmentCount: number;
	description?: string;
	displayName: string;
	id: string;
	key: string;
	organizationId: string | null;
	protected: boolean;
	rightCount: number;
	scopeKind: AthenaAuthorizationRoleScope;
	systemKind: "owner" | "admin" | "member" | null;
	version: number;
}

export interface AthenaAuthorizationRoleDetail extends AthenaAuthorizationRoleRecord {
	assignmentCount: number;
	rightCount: number;
	rights: readonly AthenaRightKey[];
}

export interface AuthorizationCapabilities {
	canChangeMemberRole: boolean;
	canDeleteOrganization: boolean;
	canInviteMembers: boolean;
	canManageOrganizationRoles: boolean;
	canManagePlatformRoles: boolean;
	canRemoveMember: boolean;
}

export interface MemberAuthorizationActions {
	canChangeRole: boolean;
	canRemove: boolean;
	denialReasons: {
		changeRole?: string;
		remove?: string;
	};
	roleDisplayName: string;
	roleKey: string;
}

export interface AuthorizationSnapshot {
	activeOrganizationId?: string;
	assignableRoles: RoleDescriptor[];
	capabilities: AuthorizationCapabilities;
	effectiveRights: readonly AthenaRightKey[];
	revision: number;
	roles: AssignedRoleSummary[];
}

export const ATHENA_AUTHORIZATION_SHADOW_KIND =
	"athena_authorization_shadow" as const;

export interface AthenaAuthorizationShadowDiagnostic {
	assignedRights: readonly string[];
	kind: typeof ATHENA_AUTHORIZATION_SHADOW_KIND;
	legacyRights: readonly string[];
	matched: boolean;
	userId: string;
}
